import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiCpu, FiPlay, FiRefreshCw, FiSend, FiUser, FiX, FiZap } from 'react-icons/fi';
import SessionService from '../services/SessionService';
import {
  listUnifiedAgents,
  type UnifiedAiAgentDTO,
} from '../services/UnifiedAIAgentService';
import {
  startSandboxSession,
  sendSandboxMessage,
  deleteSandboxSession,
  type AiSandboxSession,
  type AiSandboxMessageResult,
} from '../services/AiSandboxService';
import './AiSandboxPanel.css';

interface AiSandboxPanelProps {
  sectorId: number;
  onClose: () => void;
}

interface ChatEntry {
  id: number;
  role: 'customer' | 'ai' | 'system';
  parts: string[];
  meta?: {
    model?: string;
    latencyMs?: number;
    transferred?: boolean;
    domainDecision?: string;
    toolCalls?: string[];
    note?: string | null;
  };
}

let entrySeq = 1;

/**
 * Sandbox de teste de agente de IA: conversa simulada com o agente REAL (prompt + base de
 * conhecimento do banco), usando a mesma pipeline de produção — sem enviar WhatsApp e sem
 * tocar contatos ou memória persistente. Ideal para validar comportamento, humanização e o
 * momento do handoff antes de colocar mudanças no ar.
 */
const AiSandboxPanel: React.FC<AiSandboxPanelProps> = ({ sectorId, onClose }) => {
  const [agents, setAgents] = useState<UnifiedAiAgentDTO[]>([]);
  const [agentId, setAgentId] = useState<number | ''>('');
  const [context, setContext] = useState<'receptive' | 'campaign'>('receptive');
  const [contactName, setContactName] = useState('Cliente Teste');
  const [session, setSession] = useState<AiSandboxSession | null>(null);
  const [entries, setEntries] = useState<ChatEntry[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transferred, setTransferred] = useState(false);
  const [memory, setMemory] = useState<string | null>(null);
  const [showMemory, setShowMemory] = useState(false);
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  const token = SessionService.getToken() ?? '';

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await listUnifiedAgents(sectorId, token);
        if (!alive) return;
        const usable = list.filter(a => a.capReceptive || a.capCampaign);
        setAgents(usable);
        if (usable.length > 0) setAgentId(usable[0].id);
      } catch {
        if (alive) setError('Não foi possível carregar os agentes deste setor.');
      }
    })();
    return () => { alive = false; };
  }, [sectorId, token]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [entries]);

  const selectedAgent = useMemo(
    () => agents.find(a => a.id === agentId) ?? null,
    [agents, agentId]
  );

  const pushEntry = useCallback((entry: Omit<ChatEntry, 'id'>) => {
    setEntries(prev => [...prev, { ...entry, id: entrySeq++ }]);
  }, []);

  const handleStart = async () => {
    if (!agentId) return;
    setStarting(true);
    setError(null);
    try {
      if (session) {
        deleteSandboxSession(session.sessionId, token).catch(() => undefined);
      }
      const s = await startSandboxSession(sectorId, Number(agentId), context, contactName, token);
      setSession(s);
      setEntries([]);
      setTransferred(false);
      setMemory(null);
      pushEntry({
        role: 'system',
        parts: [
          `Sessão iniciada com "${s.agentName}" (${context === 'campaign' ? 'campanha' : 'receptiva'}). ` +
          `Converse como se fosse o cliente. Nada aqui é enviado ao WhatsApp nem afeta contatos reais.`,
        ],
      });
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Falha ao iniciar a sessão de teste.');
    } finally {
      setStarting(false);
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || !session || busy) return;
    setInput('');
    setBusy(true);
    setError(null);
    pushEntry({ role: 'customer', parts: [text] });
    try {
      const result: AiSandboxMessageResult = await sendSandboxMessage(session.sessionId, text, token);
      if (result.parts.length > 0) {
        pushEntry({
          role: 'ai',
          parts: result.parts,
          meta: {
            model: result.model,
            latencyMs: result.latencyMs,
            transferred: result.transferred,
            domainDecision: result.domainDecision,
            toolCalls: result.toolCalls,
            note: result.note,
          },
        });
      }
      if (result.note && result.parts.length === 0) {
        pushEntry({ role: 'system', parts: [result.note] });
      }
      if (result.transferred) {
        setTransferred(true);
        pushEntry({
          role: 'system',
          parts: ['🔀 A IA transferiu o atendimento para um atendente humano (marcador [TRANSFER_TO_HUMAN] detectado). Em produção, a IA ficaria desativada para este contato a partir daqui.'],
        });
      }
      if (result.memory) setMemory(result.memory);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Falha ao gerar a resposta da IA.');
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    if (session) deleteSandboxSession(session.sessionId, token).catch(() => undefined);
    onClose();
  };

  return (
    <div className="ai-sandbox-overlay" role="dialog" aria-modal="true">
      <div className="ai-sandbox-modal">
        <div className="ai-sandbox-header">
          <div className="ai-sandbox-header__title">
            <FiZap size={18} />
            <div>
              <h2>Sandbox de teste da IA</h2>
              <p>Simule uma conversa real com o agente antes de colocar no ar — nada é enviado ao WhatsApp.</p>
            </div>
          </div>
          <button type="button" className="ai-sandbox-close" onClick={handleClose} aria-label="Fechar">
            <FiX size={18} />
          </button>
        </div>

        <div className="ai-sandbox-toolbar">
          <label>
            Agente
            <select
              value={agentId}
              onChange={e => setAgentId(e.target.value ? Number(e.target.value) : '')}
              disabled={starting}
            >
              {agents.length === 0 && <option value="">Nenhum agente disponível</option>}
              {agents.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name}{!a.isEnabled ? ' (inativo)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label>
            Contexto
            <select value={context} onChange={e => setContext(e.target.value as 'receptive' | 'campaign')} disabled={starting}>
              <option value="receptive">Receptiva (chat)</option>
              <option value="campaign">Campanha (pós-disparo)</option>
            </select>
          </label>
          <label>
            Nome do cliente simulado
            <input value={contactName} onChange={e => setContactName(e.target.value)} disabled={starting} />
          </label>
          <button
            type="button"
            className="ai-sandbox-start"
            onClick={handleStart}
            disabled={!agentId || starting}
          >
            {session ? <><FiRefreshCw size={14} /> Reiniciar</> : <><FiPlay size={14} /> Iniciar sessão</>}
          </button>
        </div>

        {selectedAgent && session && (
          <div className="ai-sandbox-agentinfo">
            <FiCpu size={13} />
            <span>
              Testando <strong>{session.agentName}</strong>
              {session.model ? <> · modelo <code>{session.model}</code></> : null}
              {transferred && <span className="ai-sandbox-badge ai-sandbox-badge--transfer">transferido p/ humano</span>}
            </span>
            {memory && (
              <button type="button" className="ai-sandbox-memlink" onClick={() => setShowMemory(v => !v)}>
                {showMemory ? 'ocultar memória' : 'ver memória da IA'}
              </button>
            )}
          </div>
        )}

        {showMemory && memory && (
          <div className="ai-sandbox-memory">
            <strong>Memória da sessão (o que a IA "lembra" do cliente):</strong>
            <pre>{memory}</pre>
          </div>
        )}

        <div className="ai-sandbox-chat">
          {!session && (
            <div className="ai-sandbox-empty">
              <FiZap size={26} />
              <p>Selecione o agente e clique em <strong>Iniciar sessão</strong> para começar o teste.</p>
            </div>
          )}
          {entries.map(entry => (
            <div key={entry.id} className={`ai-sandbox-row ai-sandbox-row--${entry.role}`}>
              {entry.role !== 'system' && (
                <div className="ai-sandbox-avatar">
                  {entry.role === 'customer' ? <FiUser size={13} /> : <FiCpu size={13} />}
                </div>
              )}
              <div className="ai-sandbox-bubbles">
                {entry.parts.map((part, i) => (
                  <div key={i} className={`ai-sandbox-bubble ai-sandbox-bubble--${entry.role}`}>
                    {part}
                  </div>
                ))}
                {entry.meta && (
                  <div className="ai-sandbox-meta">
                    {entry.meta.model && <span>{entry.meta.model}</span>}
                    {typeof entry.meta.latencyMs === 'number' && <span>{(entry.meta.latencyMs / 1000).toFixed(1)}s</span>}
                    {entry.parts.length > 1 && <span>{entry.parts.length} bolhas</span>}
                    {entry.meta.toolCalls && entry.meta.toolCalls.length > 0 && (
                      <span className="ai-sandbox-meta__tool">tool: {entry.meta.toolCalls.join(', ')}</span>
                    )}
                    {entry.meta.note && <span className="ai-sandbox-meta__note">{entry.meta.note}</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="ai-sandbox-row ai-sandbox-row--ai">
              <div className="ai-sandbox-avatar"><FiCpu size={13} /></div>
              <div className="ai-sandbox-bubbles">
                <div className="ai-sandbox-bubble ai-sandbox-bubble--ai ai-sandbox-bubble--typing">digitando…</div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {error && <div className="ai-sandbox-error">{error}</div>}

        <div className="ai-sandbox-inputbar">
          <textarea
            value={input}
            placeholder={
              !session
                ? 'Inicie uma sessão para conversar…'
                : transferred
                  ? 'IA desativada nesta sessão (handoff). Reinicie para continuar testando.'
                  : 'Escreva como se fosse o cliente… (Enter envia, Shift+Enter quebra linha)'
            }
            disabled={!session || busy || transferred}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            rows={2}
          />
          <button
            type="button"
            className="ai-sandbox-send"
            onClick={handleSend}
            disabled={!session || busy || transferred || !input.trim()}
            aria-label="Enviar"
          >
            <FiSend size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AiSandboxPanel;
